<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { useListingDetailStore } from '@/modules/realestate/stores/listing.detail.store.ts'
import {
  DIRECTION_OPTIONS,
  APARTMENT_TYPE_OPTIONS,
  HOUSE_TYPE_OPTIONS,
  FURNITURE_STATUS_OPTIONS,
  HANDOVER_STATUS_OPTIONS,
  LAND_TYPE_OPTIONS,
  REAL_ESTATE_CATEGORY,
} from '@/modules/realestate/constants/realestate.const'

interface Props {
  editable: boolean
  form?: any
  newData?: any
  /** Passed from parent to avoid re-reading from fields */
  realEstateCategory?: string
  transactionType?: string
}

const listingDetailStore = useListingDetailStore()

const _attributes = computed(() => {
  try {
    return listingDetailStore.attributes?.data || {}
  } catch (e) {
    return null
  }
})

const attributes_options = ref<any[]>([])

const requestFetchListAttribute = async () => {
  try {
    await listingDetailStore.fetchAttributes()
  } catch (e) {
    console.error(e)
  }
}

onMounted(() => {
  requestFetchListAttribute()
})

const props = defineProps<Props>()
const _selected_attributes = ref<any[]>([])
const editable = computed(() => props.editable)
const initValues = computed(() => props.form?.initialValues || {})
const fields = computed(() => props.form?.fields || {})
const newData = computed(() => props.newData || null)

/** Active category: prefer prop (passed from parent), fallback to form field */
const category = computed(() =>
  props.realEstateCategory || fields.value?.real_estate_category?.states?.value?.code || ''
)

watch(
  () => initValues.value,
  (newValue: any) => {
    if (newValue) {
      _selected_attributes.value = newValue.attributes || []
    }
  },
  { immediate: true },
)

const CATEGORY_ATTRIBUTE_FALLBACKS: Record<string, string[]> = {
  house_in_alley: ['house_in_alley', 'house'],
  villa: ['villa', 'house'],
  shop_house: ['shop_house', 'house'],
  condotel: ['condotel', 'apartment'],
}

const APP_ATTRIBUTE_NAME_MAP: Record<string, string[]> = {
  apartment: ['Căn góc', 'Điều hòa', 'Tủ bếp', 'Nóng lạnh', 'Ban công', 'Sân vườn'],
  house: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy', 'Hồ bơi', 'Sân vườn', 'Sân thượng'],
  house_in_alley: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy', 'Hồ bơi', 'Sân thượng'],
  land: ['Có nở hậu', 'Có thổ cư'],
  shop_house: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy', 'Hồ bơi', 'Sân vườn', 'Sân thượng'],
  office: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy', 'Sân thượng'],
  hostel: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy'],
  villa: ['Điều hòa', 'Ban công', 'Tủ bếp', 'Nóng lạnh', 'Thang máy', 'Hồ bơi', 'Sân vườn', 'Sân thượng'],
  condotel: ['Căn góc', 'Điều hòa', 'Tủ bếp', 'Nóng lạnh', 'Ban công', 'Sân vườn'],
  building: ['Tầng hầm', 'Bãi đổ xe', 'Thang máy'],
  other: [],
}

const normalizeCheckboxOptionsFromNameMap = (categoryCode: string) => {
  const names = APP_ATTRIBUTE_NAME_MAP[categoryCode] || []
  return names.map((name, index) => ({
    id: `fallback-${categoryCode}-${index + 1}`,
    name,
  }))
}

const resolveCategoryAttributes = (raw: any, categoryCode: string) => {
  const root = raw?.data || raw || {}
  const keys = [categoryCode, ...(CATEGORY_ATTRIBUTE_FALLBACKS[categoryCode] || [])]

  for (const key of keys) {
    if (Array.isArray(root?.[key]) && root[key].length > 0) {
      return root[key]
    }
  }

  if (Array.isArray(root)) {
    for (const key of keys) {
      const filtered = root.filter((item: any) => item?.real_estate_category === key)
      if (filtered.length > 0) return filtered
    }
  }

  if (Array.isArray(root?.attributes)) {
    for (const key of keys) {
      const filtered = root.attributes.filter((item: any) => item?.real_estate_category === key)
      if (filtered.length > 0) return filtered
    }
  }

  return []
}

watch(
  () => [category.value, _attributes.value],
  ([newCategory, newAttrs]) => {
    if (!newCategory) {
      attributes_options.value = []
      return
    }

    attributes_options.value = resolveCategoryAttributes(newAttrs as any, newCategory)
  },
  { immediate: true },
)

const checkbox_attribute_options = computed(() => {
  const raw = attributes_options.value || []
  const normalized = (Array.isArray(raw) ? raw.flat(Infinity) : []) as any[]

  const fromApi = normalized
    .filter((item: any) => item?.attribute?.input_type === 'checkbox')
    .map((item: any) => ({
      id: String(item.attribute.id),
      name: item.attribute.name,
    }))

  if (fromApi.length > 0) return fromApi

  return normalizeCheckboxOptionsFromNameMap(category.value)
})

const selected_attribute_ids = computed(() => {
  return (_selected_attributes.value || [])
    .map((attr: any) => {
      if (attr?.id !== undefined && attr?.id !== null) return String(attr.id)

      const byName = checkbox_attribute_options.value.find((item: any) => item.name === attr?.name)
      if (byName) return byName.id

      const byValue = checkbox_attribute_options.value.find(
        (item: any) => item.id === String(attr?.value || ''),
      )
      return byValue?.id || ''
    })
    .filter(Boolean)
})

const getAttributeById = (id: string) => {
  return checkbox_attribute_options.value.find((item: any) => item.id === id)
}

const toggleAttribute = (id: string, checked: boolean) => {
  const attr = getAttributeById(id)
  if (!attr) return

  const current = _selected_attributes.value || []
  const exists = current.some(
    (item: any) => item?.name === attr.name || String(item?.id || '') === String(attr.id),
  )

  if (checked && !exists) {
    const numericId = Number(attr.id)
    _selected_attributes.value = [
      ...current,
      {
        id: Number.isFinite(numericId) ? numericId : null,
        name: attr.name,
        value: String(attr.id),
      },
    ]
    return
  }

  if (!checked) {
    _selected_attributes.value = current.filter(
      (item: any) => !(item?.name === attr.name || String(item?.id || '') === String(attr.id)),
    )
  }
}

const selected_attribute_names = computed(() =>
  (_selected_attributes.value || []).map((item: any) => item?.name).filter(Boolean),
)

const new_data_attribute_names = computed(() =>
  (newData.value?.attributes || []).map((item: any) => item?.name).filter(Boolean),
)

const new_data_only_attributes = computed(() => {
  if (!newData.value?.attributes) return []
  return (newData.value?.attributes || []).filter(
    (item: any) => !selected_attribute_names.value.includes(item?.name),
  )
})

// ─── Category visibility flags ─────────────────────────────────────────────────
const isApartmentLike = computed(() =>
  [REAL_ESTATE_CATEGORY.APARTMENT, REAL_ESTATE_CATEGORY.CONDOTEL].includes(category.value as REAL_ESTATE_CATEGORY)
)
const isHouseLike = computed(() =>
  [
    REAL_ESTATE_CATEGORY.HOUSE,
    REAL_ESTATE_CATEGORY.HOUSE_IN_ALLEY,
    REAL_ESTATE_CATEGORY.VILLA,
    REAL_ESTATE_CATEGORY.SHOPHOUSE,
  ].includes(category.value as REAL_ESTATE_CATEGORY)
)
const isLand = computed(() => category.value === REAL_ESTATE_CATEGORY.LAND)
const isOffice = computed(() => category.value === REAL_ESTATE_CATEGORY.OFFICE)
const isHostel = computed(() => category.value === REAL_ESTATE_CATEGORY.HOSLET)
const isBuilding = computed(() => category.value === REAL_ESTATE_CATEGORY.BUILDING)

/** Show room/bathroom for apartment-like, house-like, hostel */
const showRooms = computed(() => isApartmentLike.value || isHouseLike.value || isHostel.value)

/** Show floors for house-like */
const showFloors = computed(() => isHouseLike.value)

/** Show block + floor_number for apartment + office */
const showBlockFloor = computed(() => isApartmentLike.value || isOffice.value)

/** Show handover_status for apartment-like only */
const showHandover = computed(() => isApartmentLike.value)

/** Show property_type for apartment-like + house-like + office + hostel (NOT building) */
const showPropertyType = computed(() =>
  isApartmentLike.value || isHouseLike.value || isOffice.value || isHostel.value
)

/** Get property type options based on category */
const propertyTypeOptions = computed(() => {
  if (isApartmentLike.value) return APARTMENT_TYPE_OPTIONS
  if (isHouseLike.value) return HOUSE_TYPE_OPTIONS
  return APARTMENT_TYPE_OPTIONS
})

/** Show furniture_status for all except land */
const showFurniture = computed(() => !isLand.value && category.value !== '')

/** Show balcony_direction for apartment-like */
const showBalcony = computed(() => isApartmentLike.value)

/** Show land_type for land only */
const showLandType = computed(() => isLand.value)

/** Show unit_number for apartment only */
const showUnitNumber = computed(() => isApartmentLike.value)

/** Show estimated_cashflow for building only */
const showEstimatedCashflow = computed(() => isBuilding.value)

// ─── Helper: find label from options ──────────────────────────────────────────
const findLabel = (options: any[], value: string | null) => {
  if (!value) return ''
  return options.find((o) => o.value === value)?.label || value
}

const formControlOldClass = '!bg-red-100 !box-border !px-2 !py-1'
const formControlNewClass = 'form-control !bg-green-100 !text-green-800 !px-2 !py-1'

// Computed properties for land-specific attributes
const hasNoHau = computed(() => 
  selected_attribute_names.value.includes('Có nở hậu')
)

const hasThoCu = computed(() => 
  selected_attribute_names.value.includes('Có thổ cư')
)

// Computed properties for building-specific attributes
const hasElevator = computed(() => 
  selected_attribute_names.value.includes('Thang máy')
)
</script>

<template>
  <!-- ─── Category-specific structured fields ──────────────────────────────── -->
  
  <!-- Loại hình BĐS & Căn số - 2 cột -->
  <div class="grid md:grid-cols-2 gap-4 mb-4" v-if="showPropertyType">
      <!-- Loại hình BĐS (property_type) -->
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Loại hình BĐS:</div>
        <div class="w-full space-y-1">
          <Select
            name="property_type"
            :options="propertyTypeOptions.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn loại hình"
            :class="`form-control ${
              newData && fields?.property_type?.states?.value?.code !== newData?.property_type
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.property_type?.states?.value?.code !== newData?.property_type">
            <InputText
              :value="findLabel(propertyTypeOptions, newData?.property_type)"
              disabled
              :class="formControlNewClass"
            />
          </template>
        </div>
      </div>

      <!-- Căn số (unit_number) - Chỉ cho căn hộ -->
      <div class="flex flex-col gap-1" v-if="showUnitNumber">
        <div class="font-medium mb-1">Căn số:</div>
        <div class="w-full space-y-1">
          <InputText
            name="unit_number"
            placeholder="Ví dụ: A12"
            :class="`form-control ${
              newData && fields?.unit_number?.states?.value !== newData?.unit_number
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.unit_number?.states?.value !== newData?.unit_number">
            <InputText :value="newData?.unit_number" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>
    </div>

    <!-- Tình trạng bàn giao & Nội thất - 2 cột -->
    <div class="grid md:grid-cols-2 gap-4 mb-4">
      <!-- Tình trạng bàn giao (handover_status) — apartment only -->
      <div class="flex flex-col gap-1" v-if="showHandover">
        <div class="font-medium mb-1">Tình trạng bàn giao:</div>
        <div class="w-full space-y-1">
          <Select
            name="handover_status"
            :options="HANDOVER_STATUS_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn tình trạng"
            :class="`form-control ${
              newData && fields?.handover_status?.states?.value?.code !== newData?.handover_status
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.handover_status?.states?.value?.code !== newData?.handover_status">
            <InputText
              :value="findLabel(HANDOVER_STATUS_OPTIONS, newData?.handover_status)"
              disabled
              :class="formControlNewClass"
            />
          </template>
        </div>
      </div>

      <!-- Tình trạng nội thất (furniture_status) -->
      <div class="flex flex-col gap-1" v-if="showFurniture">
        <div class="font-medium mb-1">Nội thất:</div>
        <div class="w-full space-y-1">
          <Select
            name="furniture_status"
            :options="FURNITURE_STATUS_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn tình trạng nội thất"
            :class="`form-control ${
              newData && fields?.furniture_status?.states?.value?.code !== newData?.furniture_status
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.furniture_status?.states?.value?.code !== newData?.furniture_status">
            <InputText
              :value="findLabel(FURNITURE_STATUS_OPTIONS, newData?.furniture_status)"
              disabled
              :class="formControlNewClass"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Số phòng ngủ & phòng tắm - 2 cột -->
    <div class="grid grid-cols-2 gap-4 mb-4" v-if="showRooms">
      <!-- Số phòng ngủ -->
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Số phòng ngủ:</div>
        <div class="w-full space-y-1">
          <InputNumber
            name="rooms"
            placeholder="Số phòng ngủ"
            :min="0"
            :class="`form-control ${
              newData && fields?.rooms?.states?.value !== newData?.rooms ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.rooms?.states?.value !== newData?.rooms">
            <InputText :value="newData?.rooms" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>

      <!-- Số phòng tắm / vệ sinh -->
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Số phòng tắm:</div>
        <div class="w-full space-y-1">
          <InputNumber
            name="bath_rooms"
            placeholder="Số phòng tắm"
            :min="0"
            :class="`form-control ${
              newData && fields?.bath_rooms?.states?.value !== newData?.bath_rooms ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.bath_rooms?.states?.value !== newData?.bath_rooms">
            <InputText :value="newData?.bath_rooms" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>
    </div>

    <!-- Số tầng (house-like) -->
    <div class="flex flex-col gap-1 mb-4" v-if="showFloors">
      <div class="font-medium mb-1">Số tầng:</div>
      <div class="w-full space-y-1">
        <InputNumber
          name="floors"
          placeholder="Số tầng"
          :min="0"
          :class="`form-control ${
            newData && fields?.floors?.states?.value !== newData?.floors ? formControlOldClass : ''
          }`"
          :disabled="!editable"
        />
        <template v-if="newData && fields?.floors?.states?.value !== newData?.floors">
          <InputText :value="newData?.floors" disabled :class="formControlNewClass" />
        </template>
      </div>
    </div>

    <!-- Block / Tầng (apartment / office) - 2 cột -->
    <div class="grid grid-cols-2 gap-4 mb-4" v-if="showBlockFloor">
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Block / Tòa:</div>
        <div class="w-full space-y-1">
          <InputText
            name="block_number"
            placeholder="Tên block / tòa nhà"
            :class="`form-control ${
              newData && fields?.block_number?.states?.value !== newData?.block_number
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.block_number?.states?.value !== newData?.block_number">
            <InputText :value="newData?.block_number" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Tầng:</div>
        <div class="w-full space-y-1">
          <InputText
            name="floor_number"
            placeholder="Số tầng (vị trí căn hộ)"
            :class="`form-control ${
              newData && fields?.floor_number?.states?.value !== newData?.floor_number
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.floor_number?.states?.value !== newData?.floor_number">
            <InputText :value="newData?.floor_number" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>
    </div>

    <!-- Hướng đất (dành cho land) -->
    <div class="flex flex-col gap-1 mb-4" v-if="isLand">
      <div class="font-medium mb-1">Hướng đất:</div>
      <div class="w-full space-y-1">
        <Select
          name="main_door_direction"
          :options="DIRECTION_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
          optionLabel="name"
          placeholder="Chọn hướng đất"
          :class="`form-control ${
            newData && fields?.main_door_direction?.states?.value !== newData?.main_door_direction
              ? formControlOldClass : ''
          }`"
          :disabled="!editable"
        />
        <template v-if="newData && fields?.main_door_direction?.states?.value !== newData?.main_door_direction">
          <InputText
            :value="findLabel(DIRECTION_OPTIONS, newData?.main_door_direction)"
            disabled
            :class="formControlNewClass"
          />
        </template>
      </div>
    </div>

    <!-- Hướng cửa chính và Hướng ban công - 2 cột -->
    <div class="grid grid-cols-2 gap-4 mb-4" v-if="isApartmentLike || isOffice">
      <div class="flex flex-col gap-1">
        <div class="font-medium mb-1">Hướng cửa chính:</div>
        <div class="w-full space-y-1">
          <Select
            name="main_door_direction"
            :options="DIRECTION_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn hướng cửa chính"
            :class="`form-control ${
              newData && fields?.main_door_direction?.states?.value !== newData?.main_door_direction
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.main_door_direction?.states?.value !== newData?.main_door_direction">
            <InputText
              :value="findLabel(DIRECTION_OPTIONS, newData?.main_door_direction)"
              disabled
              :class="formControlNewClass"
            />
          </template>
        </div>
      </div>
      <div class="flex flex-col gap-1" v-if="showBalcony">
        <div class="font-medium mb-1">Hướng ban công:</div>
        <div class="w-full space-y-1">
          <Select
            name="balcony_direction"
            :options="DIRECTION_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
            optionLabel="name"
            placeholder="Chọn hướng ban công"
            :class="`form-control ${
              newData && fields?.balcony_direction?.states?.value !== newData?.balcony_direction
                ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.balcony_direction?.states?.value !== newData?.balcony_direction">
            <InputText
              :value="findLabel(DIRECTION_OPTIONS, newData?.balcony_direction)"
              disabled
              :class="formControlNewClass"
            />
          </template>
        </div>
      </div>
    </div>

    <!-- Loại đất (land_type) — land only -->
    <div class="flex flex-col gap-1 mb-4" v-if="showLandType">
      <div class="font-medium mb-1">Loại đất:</div>
      <div class="w-full space-y-1">
        <Select
          name="land_type"
          :options="LAND_TYPE_OPTIONS.map((item: any) => ({ name: item.label, code: item.value }))"
          optionLabel="name"
          placeholder="Chọn loại đất"
          :class="`form-control ${
            newData && fields?.land_type?.states?.value?.code !== newData?.land_type
              ? formControlOldClass : ''
          }`"
          :disabled="!editable"
        />
        <template v-if="newData && fields?.land_type?.states?.value?.code !== newData?.land_type">
          <InputText
            :value="findLabel(LAND_TYPE_OPTIONS, newData?.land_type)"
            disabled
            :class="formControlNewClass"
          />
        </template>
      </div>
    </div>

    <!-- Dòng tiền ước tính (estimated_cashflow) — building only -->
    <div class="flex flex-col gap-1 mb-4" v-if="showEstimatedCashflow">
      <div class="font-medium mb-1">Dòng tiền ước tính (tỷ/tháng):</div>
      <div class="w-full space-y-1">
        <InputNumber
          name="estimated_cashflow"
          placeholder="Nhập dòng tiền ước tính"
          :min="0"
          :minFractionDigits="2"
          :maxFractionDigits="2"
          :class="`form-control ${
            newData && fields?.estimated_cashflow?.states?.value !== newData?.estimated_cashflow
              ? formControlOldClass : ''
          }`"
          :disabled="!editable"
        />
        <template v-if="newData && fields?.estimated_cashflow?.states?.value !== newData?.estimated_cashflow">
          <InputText :value="newData?.estimated_cashflow" disabled :class="formControlNewClass" />
        </template>
      </div>
    </div>

  <!-- ─── Checkbox attributes (sync App behavior) ───────────────────────────── -->
  <div class="flex flex-col gap-1 mb-4">
    <input type="hidden" id="listing_hidden_attributes" :value="JSON.stringify(_selected_attributes)" />
    <div class="font-medium mb-1">Công năng:</div>

    <div class="w-full space-y-2">
      <template v-if="checkbox_attribute_options.length">
        <div class="grid grid-cols-3 gap-3">
          <div
            v-for="item in checkbox_attribute_options"
            :key="item.id"
            class="flex items-center gap-2"
          >
            <Checkbox
              binary
              :inputId="`attr-${item.id}`"
              :modelValue="selected_attribute_ids.includes(item.id)"
              :disabled="!editable"
              @update:modelValue="(checked) => toggleAttribute(item.id, checked)"
            />
            <label :for="`attr-${item.id}`" class="cursor-pointer text-sm">{{ item.name }}</label>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="text-sm text-gray-500 italic">Không có công năng cho loại BĐS này.</div>
      </template>

      <template v-if="newData && newData?.attributes">
        <div class="flex flex-col gap-1 mt-2">
          <div
            v-for="(attribute, index) in _selected_attributes.filter(
              (attr: any) => new_data_attribute_names.includes(attr.name) && !newData?.attributes?.some((n: any) => n.name === attr.name && String(n.value || '') === String(attr.value || '')),
            )"
            :key="`old-${index}-${attribute.name}`"
            class="form-control !bg-red-100 !box-border !px-2 !py-1"
          >
            {{ attribute.name }}
          </div>

          <div
            v-for="(attribute, index) in new_data_only_attributes"
            :key="`new-${index}-${attribute.name}`"
            class="form-control !bg-green-100 !text-green-800 !px-2 !py-1"
          >
            {{ attribute.name }}
          </div>
        </div>
      </template>
    </div>
  </div>

  <!-- Fields bổ sung sau công năng -->

  <!-- Fields bổ sung động dựa trên công năng -->
  <div v-if="(isLand && (hasNoHau || hasThoCu)) || (isBuilding && hasElevator)">
    <div class="grid md:grid-cols-2 gap-4 mb-4">
      <!-- Kích thước nở hậu (hiển thị khi chọn "Có nở hậu") -->
      <div class="flex flex-col gap-1" v-if="isLand && hasNoHau">
        <div class="font-medium mb-1">Kích thước nở hậu (m²):</div>
        <div class="w-full space-y-1">
          <InputNumber
            name="expansion_size"
            placeholder="Nhập kích thước nở hậu"
            :min="0"
            :class="`form-control ${
              newData && fields?.expansion_size?.states?.value !== newData?.expansion_size ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.expansion_size?.states?.value !== newData?.expansion_size">
            <InputText :value="newData?.expansion_size" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>

      <!-- Diện tích thổ cư (hiển thị khi chọn "Có thổ cư") -->
      <div class="flex flex-col gap-1" v-if="isLand && hasThoCu">
        <div class="font-medium mb-1">Diện tích thổ cư (m²):</div>
        <div class="w-full space-y-1">
          <InputNumber
            name="residential_area"
            placeholder="Nhập diện tích thổ cư"
            :min="0"
            :class="`form-control ${
              newData && fields?.residential_area?.states?.value !== newData?.residential_area ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.residential_area?.states?.value !== newData?.residential_area">
            <InputText :value="newData?.residential_area" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>

      <!-- Số lượng thang máy (hiển thị khi chọn "Thang máy" cho building) -->
      <div class="flex flex-col gap-1" v-if="isBuilding && hasElevator">
        <div class="font-medium mb-1">Số lượng thang máy:</div>
        <div class="w-full space-y-1">
          <InputNumber
            name="elevator_count"
            placeholder="Nhập số lượng thang máy"
            :min="1"
            :class="`form-control ${
              newData && fields?.elevator_count?.states?.value !== newData?.elevator_count ? formControlOldClass : ''
            }`"
            :disabled="!editable"
          />
          <template v-if="newData && fields?.elevator_count?.states?.value !== newData?.elevator_count">
            <InputText :value="newData?.elevator_count" disabled :class="formControlNewClass" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
